import React, { useState, useEffect, useRef, useMemo } from 'react';
import PianoRoll from './viz/PianoRoll';
import LazyMount from './viz/LazyMount';
import { pauseOtherAudio, useResolvedAudioSrc } from './viz/pianoRollShared';
import {
  CONTROL_ATTRS,
  DEFAULTS,
  EXAMPLE_IDS,
  GEN_MODES,
  MODELS,
  formatValueName,
  getAttributeDisplayName,
  getAttributeValues,
  getAudioPath,
  getAvailableModels,
  getNotesPath,
  getPromptPath,
  getResponsePath,
  isControlAttrStaged,
  isExampleIdStaged,
  isGenModeStaged,
  isModelStaged,
  isStaged,
} from './data/leadsheetExamples';

// Renders the prompt with the line carrying the controlled attribute picked
// out, so it is obvious what changes from card to card.
const HighlightedPrompt = ({ text, controlAttr }) => {
  const jsonMatch = text.match(/\{[\s\S]*\}/);
  if (!jsonMatch) return <span>{text}</span>;

  const before = text.slice(0, jsonMatch.index);
  const lines = jsonMatch[0].split('\n');

  return (
    <>
      <span className="ls-prompt-preamble">{before}</span>
      <span className="ls-prompt-json">
        {lines.map((line, i) => (
          <span
            key={i}
            className={line.includes(`"${controlAttr}"`) ? 'ls-prompt-highlight' : ''}
          >
            {line}{i < lines.length - 1 ? '\n' : ''}
          </span>
        ))}
      </span>
    </>
  );
};

const LeadSheetCard = ({ selection, attributeValue, displayName, available }) => {
  const params = { ...selection, attributeValue };
  const audioRef = useRef(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [promptText, setPromptText] = useState(null);
  const [infillSpan, setInfillSpan] = useState(null);

  const promptPath = getPromptPath(params);
  const responsePath = getResponsePath(params);
  const [audioSrc, refreshAudioSrc] = useResolvedAudioSrc(getAudioPath(params));

  useEffect(() => {
    setPromptText(null);
    setShowPrompt(false);
  }, [promptPath]);

  useEffect(() => {
    if (!available || !showPrompt || promptText !== null) return undefined;

    let cancelled = false;
    fetch(promptPath)
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error('not found'))))
      .then((text) => { if (!cancelled) setPromptText(text); })
      .catch(() => { if (!cancelled) setPromptText('(prompt not available)'); });

    return () => { cancelled = true; };
  }, [available, showPrompt, promptPath, promptText]);

  useEffect(() => {
    if (!available || selection.genMode !== 'infill_both') {
      setInfillSpan(null);
      return undefined;
    }

    let cancelled = false;
    fetch(responsePath)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('not found'))))
      .then((data) => {
        if (cancelled) return;
        const req = data?.log?.request;
        if (req && req.span_measure_start != null && req.span_measure_end != null) {
          setInfillSpan({ start: req.span_measure_start, end: req.span_measure_end });
        }
      })
      .catch(() => { if (!cancelled) setInfillSpan(null); });

    return () => { cancelled = true; };
  }, [available, selection.genMode, responsePath]);

  if (!available) {
    return (
      <div className="ls-card ls-card-unavailable">
        <div className="ls-card-header">
          <h4>{displayName}</h4>
        </div>
        <div className="ls-unavailable-body">
          <p>Not bundled with this build</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ls-card">
      <div className="ls-card-header">
        <h4>{displayName}</h4>
        <div className="ls-card-actions">
          {infillSpan && (
            <span className="ls-infill-badge">
              Infill: bars {infillSpan.start + 1}&ndash;{infillSpan.end}
            </span>
          )}
          <button
            type="button"
            className={`ls-prompt-toggle ${showPrompt ? 'active' : ''}`}
            onClick={() => setShowPrompt((v) => !v)}
          >
            Prompt
          </button>
        </div>
      </div>

      {showPrompt && promptText && (
        <pre className="ls-prompt-text">
          <HighlightedPrompt text={promptText} controlAttr={selection.controlAttr} />
        </pre>
      )}

      <LazyMount>
        <PianoRoll
          jsonUrl={getNotesPath(params)}
          audioRef={audioRef}
          infillSpan={infillSpan}
        />
      </LazyMount>

      <div className="audio-player-container">
        <audio
          ref={audioRef}
          controls
          className="simple-audio-player"
          preload="metadata"
          src={audioSrc || undefined}
          onPlay={pauseOtherAudio}
          onError={refreshAudioSrc}
        >
          Your browser does not support the audio element.
        </audio>
      </div>
    </div>
  );
};

const LeadSheetExamples = ({ onReturnToLive }) => {
  const [genMode, setGenMode] = useState(DEFAULTS.genMode);
  const [model, setModel] = useState(DEFAULTS.model);
  const [controlAttr, setControlAttr] = useState(DEFAULTS.controlAttr);
  const [exampleId, setExampleId] = useState(DEFAULTS.exampleId);

  const availableModels = useMemo(() => getAvailableModels(genMode), [genMode]);

  useEffect(() => {
    if (!availableModels.find((m) => m.key === model)) {
      setModel(availableModels[0].key);
    }
  }, [availableModels, model]);

  const selection = { model, genMode, controlAttr, exampleId };
  const available = isStaged(selection);
  const attributeValues = getAttributeValues(controlAttr);

  const suffix = (staged) => (staged ? '' : '  (not bundled)');

  return (
    <div className="static-demo-section">
      <div className="static-demo-header">
        <p>
          Lead sheets generated from <strong>TheoryTab</strong> song structures with explicit
          attribute control. Every card below shares the same key and tempo, and differs only in
          the highlighted control value, so you can hear one attribute move at a time.
          Melody and harmony are colored separately in the roll.
        </p>
      </div>

      <div className="ls-mode-toggle">
        {Object.values(GEN_MODES).map((mode) => (
          <button
            key={mode.key}
            type="button"
            className={`ls-mode-btn ${genMode === mode.key ? 'active' : ''} ${isGenModeStaged(mode.key) ? '' : 'unstaged'}`}
            onClick={() => setGenMode(mode.key)}
          >
            <span className="ls-mode-name">{mode.displayName}</span>
            <span className="ls-mode-desc">{mode.description}</span>
          </button>
        ))}
      </div>

      <div className="ls-controls">
        <div className="ls-control-group">
          <label htmlFor="ls-model">Model / Setting</label>
          <select
            id="ls-model"
            className="control-select"
            value={model}
            onChange={(e) => setModel(e.target.value)}
          >
            {availableModels.map((m) => (
              <option key={m.key} value={m.key}>
                {m.displayName}{suffix(isModelStaged(m.key))}
              </option>
            ))}
          </select>
        </div>

        <div className="ls-control-group">
          <label htmlFor="ls-attr">Control Attribute</label>
          <select
            id="ls-attr"
            className="control-select"
            value={controlAttr}
            onChange={(e) => setControlAttr(e.target.value)}
          >
            {Object.entries(CONTROL_ATTRS).map(([key, { displayName }]) => (
              <option key={key} value={key}>
                {displayName}{suffix(isControlAttrStaged(key))}
              </option>
            ))}
          </select>
        </div>

        <div className="ls-control-group">
          <label htmlFor="ls-example">Example Song ID</label>
          <select
            id="ls-example"
            className="control-select"
            value={exampleId}
            onChange={(e) => setExampleId(parseInt(e.target.value, 10))}
          >
            {EXAMPLE_IDS.map((id) => (
              <option key={id} value={id}>
                {id}{suffix(isExampleIdStaged(id))}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="ls-info">
        <strong>{getAttributeDisplayName(controlAttr)}:</strong> {attributeValues.length} values
        {' · '}Song {exampleId}
        {' · '}{GEN_MODES[genMode].displayName}
        {' · '}{MODELS[model].displayName}
      </div>

      {!available && (
        <div className="ls-availability-note">
          This combination is not bundled with the deployed site. Only the landing selection
          (<strong>MIDI-LLM, no CFG · From Scratch · Genre · Song 39673</strong>) ships with the
          app; the full corpus is several gigabytes and will be served from external storage.
        </div>
      )}

      <div className="ls-grid">
        {attributeValues.map((value) => (
          <LeadSheetCard
            key={`${model}-${genMode}-${controlAttr}-${value}-${exampleId}`}
            selection={selection}
            attributeValue={value}
            displayName={formatValueName(value, controlAttr)}
            available={available}
          />
        ))}
      </div>

      <div className="static-demo-footer">
        <p className="demo-note">
          💡 All outputs under one song ID share the same key and tempo, which are given to the
          model as controls alongside the highlighted attribute.
        </p>
        {onReturnToLive && (
          <button
            className="return-to-live-btn-large"
            onClick={onReturnToLive}
          >
            🪗 Go Play with Live Demo !!
          </button>
        )}
      </div>
    </div>
  );
};

export default LeadSheetExamples;
